// src/components/CompanyManagement.tsx - Enhanced with billing and registration codes
import React, { useState, useEffect } from 'react';
import { Building2, Plus, Edit2, Users, Eye, X, CheckCircle, AlertTriangle, DollarSign, Key, Calendar, Monitor } from 'lucide-react';
import { authApi, Company, SuperAdminStats, CreateCustomerCompanyRequest, CreateCompanyResponse, UpdateCompanyRequest } from '../services/authApi';
import { CompanyType } from '../types/companies';

import { Trash2 } from 'lucide-react';
interface CompanyManagementProps {
  onClose: () => void;
}

const CompanyManagement: React.FC<CompanyManagementProps> = ({ onClose }) => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [stats, setStats] = useState<SuperAdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'add'>('all');
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [showCompanyDetails, setShowCompanyDetails] = useState(false);
  const [creating, setCreating] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // 2. Add these state variables to your component
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [createForm, setCreateForm] = useState<CreateCustomerCompanyRequest>({
    companyName: '',
    contactEmail: '',
    contactPhone: '',
    address: '',
    city: '',
    postCode: '',
    dateOfSale: new Date().toISOString().split('T')[0], // Today's date
    monthlyRental: 20.00,
    otherCharges: 0,
    devicesLicensed: 1
  });

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [editForm, setEditForm] = useState<UpdateCompanyRequest>({
    companyName: '',
    contactEmail: '',
    contactPhone: '',
    address: '',
    city: '',
    postCode: '',
    dateOfSale: '',
    monthlyRental: 0,
    otherCharges: 0,
    devicesLicensed: 0
  });
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [allCompanies, statsData] = await Promise.all([
        authApi.getAllCompanies(),
        authApi.getSuperAdminStats()
      ]);
      
      setCompanies(allCompanies);
      setStats(statsData);
      setError(null);
    } catch (err: any) {
      setError('Failed to load company data');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!createForm.companyName || !createForm.contactEmail) {
      setError('Company name and contact email are required');
      return;
    }

    if (createForm.monthlyRental <= 0 || createForm.devicesLicensed <= 0) {
      setError('Monthly rental and devices licensed must be greater than 0');
      return;
    }

    try {
      setCreating(true);
      setError(null);
      
      const response: CreateCompanyResponse = await authApi.createCustomerCompany(createForm);
      
      await fetchData();
      setCreateForm({
        companyName: '',
        contactEmail: '',
        contactPhone: '',
        address: '',
        city: '',
        postCode: '',
        dateOfSale: new Date().toISOString().split('T')[0],
        monthlyRental: 20.00,
        otherCharges: 0,
        devicesLicensed: 1
      });
      setActiveTab('all');
      
      setSuccessMessage(
        `Company "${response.companyName}" created successfully! Registration code: ${response.registrationCode}`
      );
      setTimeout(() => setSuccessMessage(null), 10000);
      
    } catch (err: any) {
      const errorMessage = err.response?.data || err.message || 'Failed to create company';
      setError(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  // Add these handler functions:
  const handleEditCompany = (company: Company) => {
    setEditingCompany(company);
    setEditForm({
      companyName: company.companyName,
      contactEmail: company.contactEmail,
      contactPhone: company.contactPhone || '',
      address: company.address || '',
      city: company.city || '',
      postCode: company.postCode || '',
      dateOfSale: company.dateOfSale ? new Date(company.dateOfSale).toISOString().split('T')[0] : '',
      monthlyRental: company.monthlyRental || 0,
      otherCharges: company.otherCharges || 0,
      devicesLicensed: company.devicesLicensed || 0
    });
    setShowEditModal(true);
  };

  const handleUpdateCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingCompany) return;
    
    if (!editForm.companyName || !editForm.contactEmail) {
      setError('Company name and contact email are required');
      return;
    }

    // Only validate billing fields for customer companies
    if (editingCompany.companyType === CompanyType.Customer) {
      if (editForm.monthlyRental && editForm.monthlyRental <= 0) {
        setError('Monthly rental must be greater than 0');
        return;
      }

      if (editForm.devicesLicensed && editForm.devicesLicensed <= 0) {
        setError('Devices licensed must be greater than 0');
        return;
      }
    }

    try {
      setUpdating(true);
      setError(null);
      
      await authApi.updateCompany(editingCompany.companyId, editForm);
      
      await fetchData();
      setShowEditModal(false);
      setEditingCompany(null);
      
      setSuccessMessage(`Company "${editForm.companyName}" updated successfully!`);
      setTimeout(() => setSuccessMessage(null), 5000);
      
    } catch (err: any) {
      const errorMessage = err.response?.data || err.message || 'Failed to update company';
      setError(errorMessage);
    } finally {
      setUpdating(false);
    }
  };

  // 3. Add this delete handler function
  const handleDeleteCompany = async () => {
    if (!companyToDelete) return;
    
    setIsDeleting(true);
    try {
      await authApi.deleteCompany(companyToDelete.companyId);
      
      // Show success message
      alert(`Company "${companyToDelete.companyName}" and all related data deleted successfully`);
      
      // Refresh the companies list
      await fetchData();
      
      // Close modal and reset state
      setShowDeleteModal(false);
      setCompanyToDelete(null);
      
      // Close detail modal if it was open for the deleted company
      if (selectedCompany?.companyId === companyToDelete.companyId) {
        setSelectedCompany(null);
      }
    } catch (error) {
      console.error('Error deleting company:', error);
      alert('Error deleting company. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // 4. Add this function to open the delete confirmation
  const openDeleteConfirmation = (company: Company) => {
    setCompanyToDelete(company);
    setShowDeleteModal(true);
  };

  const handleViewCompanyDetails = async (company: Company) => {
    setSelectedCompany(company);
    setShowCompanyDetails(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const getCompanyTypeLabel = (type: CompanyType) => {
    return type === CompanyType.Hosting ? 'Hosting' : 'Customer';
  };

  const getStatusBadge = (isActive: boolean) => {
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${
        isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
      }`}>
        {isActive ? 'Active' : 'Inactive'}
      </span>
    );
  };

  const getRegistrationBadge = (company: Company) => {
    if (company.companyType === CompanyType.Hosting) {
      return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">Hosting</span>;
    }
    
    if (company.registrationCodeUsed) {
      return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Code Used</span>;
    }
    
    return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">Awaiting Registration</span>;
  };

  const renderAllCompanies = () => (
    <div className="space-y-4">
      {companies.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No companies found
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Billing
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Devices
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Registration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {companies.map((company) => (
                <tr key={company.companyId} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {company.companyName}
                      </div>
                      <div className="text-sm text-gray-500">{company.contactEmail}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      company.companyType === CompanyType.Hosting 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-purple-100 text-purple-800'
                    }`}>
                      {getCompanyTypeLabel(company.companyType)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(company.isActive)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {company.monthlyRental ? (
                      <div className="text-sm">
                        <div className="font-medium text-gray-900">
                          {formatCurrency(company.monthlyRental)}
                        </div>
                        <div className="text-gray-500">monthly</div>
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">N/A</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      <div className="text-gray-900">
                        {company.deviceCount} / {company.devicesLicensed || 'N/A'}
                      </div>
                      <div className="text-gray-500">active / licensed</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getRegistrationBadge(company)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleViewCompanyDetails(company)}
                        className="text-blue-600 hover:text-blue-800"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEditCompany(company)}
                        className="text-gray-600 hover:text-gray-800"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                        {/* ADD THIS DELETE BUTTON HERE */}
                      <button
                        onClick={() => openDeleteConfirmation(company)}
                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete Company"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderCreateForm = () => (
    <div className="bg-white rounded-lg p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-6">Create New Customer Company</h3>
      
      <form onSubmit={handleCreateCompany} className="space-y-6">
        {/* Company Information */}
        <div>
          <h4 className="text-md font-medium text-gray-900 mb-4">Company Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Company Name *</label>
              <input
                type="text"
                required
                value={createForm.companyName}
                onChange={(e) => setCreateForm({ ...createForm, companyName: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Contact Email *</label>
              <input
                type="email"
                required
                value={createForm.contactEmail}
                onChange={(e) => setCreateForm({ ...createForm, contactEmail: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Contact Phone</label>
              <input
                type="tel"
                value={createForm.contactPhone}
                onChange={(e) => setCreateForm({ ...createForm, contactPhone: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Post Code</label>
              <input
                type="text"
                value={createForm.postCode}
                onChange={(e) => setCreateForm({ ...createForm, postCode: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700">Address</label>
            <input
              type="text"
              value={createForm.address}
              onChange={(e) => setCreateForm({ ...createForm, address: e.target.value })}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700">City</label>
            <input
              type="text"
              value={createForm.city}
              onChange={(e) => setCreateForm({ ...createForm, city: e.target.value })}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Billing Information */}
        <div className="border-t pt-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">Billing Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Date of Sale *</label>
              <input
                type="date"
                required
                value={createForm.dateOfSale}
                onChange={(e) => setCreateForm({ ...createForm, dateOfSale: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Monthly Rental (£) *</label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                value={createForm.monthlyRental}
                onChange={(e) => setCreateForm({ ...createForm, monthlyRental: parseFloat(e.target.value) })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Other Charges (£)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={createForm.otherCharges}
                onChange={(e) => setCreateForm({ ...createForm, otherCharges: parseFloat(e.target.value) || 0 })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Setup fees, etc."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Devices Licensed *</label>
              <input
                type="number"
                required
                min="1"
                value={createForm.devicesLicensed}
                onChange={(e) => setCreateForm({ ...createForm, devicesLicensed: parseInt(e.target.value) })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={() => setActiveTab('all')}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={creating}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {creating ? 'Creating...' : 'Create Company'}
          </button>
        </div>
      </form>
    </div>
  );

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden max-h-[90vh] flex flex-col">
      <div className="flex justify-between items-center p-6 border-b">
        <div className="flex items-center">
          <Building2 className="w-5 h-5 text-blue-500 mr-2" />
          <h2 className="text-xl font-semibold">Company Management</h2>
        </div>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
          <X className="w-5 h-5" />
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 text-red-700 border-b">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2" />
            {error}
          </div>
        </div>
      )}

      {successMessage && (
        <div className="p-4 bg-green-50 text-green-700 border-b">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 mr-2" />
            {successMessage}
          </div>
        </div>
      )}

      {/* Stats Bar */}
      {stats && (
        <div className="p-4 bg-gray-50 border-b">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.totalCompanies}</div>
              <div className="text-xs text-gray-600">Total Companies</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">{stats.activeCompanies}</div>
              <div className="text-xs text-gray-600">Active</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-yellow-600">{stats.pendingCompanies}</div>
              <div className="text-xs text-gray-600">Pending</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-600">{stats.totalDevices}</div>
              <div className="text-xs text-gray-600">Total Devices</div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 px-6">
          {[
            { id: 'all', name: 'All Companies', count: stats?.totalCompanies },
            { id: 'add', name: 'Add Company', count: null }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.name}
              {tab.count != null && tab.count > 0 && (
                <span className="ml-2 bg-gray-200 text-gray-900 py-0.5 px-2 rounded-full text-xs">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading companies...</div>
        ) : activeTab === 'add' ? (
          renderCreateForm()
        ) : (
          renderAllCompanies()
        )}
      </div>

      {/* Company Details Modal */}
      {showCompanyDetails && selectedCompany && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">Company Details</h3>
                <button 
                  onClick={() => setShowCompanyDetails(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-6">
                {/* Basic Information */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Basic Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Company Name</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedCompany.companyName}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Type</label>
                      <p className="mt-1 text-sm text-gray-900">{getCompanyTypeLabel(selectedCompany.companyType)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Contact Email</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedCompany.contactEmail}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Status</label>
                      <div className="mt-1">{getStatusBadge(selectedCompany.isActive)}</div>
                    </div>
                  </div>
                </div>
                
                {/* Billing Information */}
                {selectedCompany.monthlyRental && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                      <DollarSign className="w-4 h-4 mr-2" />
                      Billing Information
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Monthly Rental</label>
                        <p className="mt-1 text-sm text-gray-900">{formatCurrency(selectedCompany.monthlyRental)}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Date of Sale</label>
                        <p className="mt-1 text-sm text-gray-900">
                          {selectedCompany.dateOfSale ? formatDate(selectedCompany.dateOfSale) : 'Not set'}
                        </p>
                      </div>
                      {selectedCompany.otherCharges && selectedCompany.otherCharges > 0 && (
                        <div>
                          <label className="text-sm font-medium text-gray-700">Other Charges</label>
                          <p className="mt-1 text-sm text-gray-900">{formatCurrency(selectedCompany.otherCharges)}</p>
                        </div>
                      )}
                      <div>
                        <label className="text-sm font-medium text-gray-700">Devices Licensed</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedCompany.devicesLicensed || 'Not set'}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Registration Information */}
                {selectedCompany.registrationCode && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                      <Key className="w-4 h-4 mr-2" />
                      Registration Information
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Registration Code</label>
                        <p className="mt-1 text-sm text-gray-900 font-mono">{selectedCompany.registrationCode}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Code Status</label>
                        <div className="mt-1">{getRegistrationBadge(selectedCompany)}</div>
                      </div>
                      {selectedCompany.registrationCodeUsedDate && (
                        <>
                          <div>
                            <label className="text-sm font-medium text-gray-700">Used Date</label>
                            <p className="mt-1 text-sm text-gray-900">{formatDate(selectedCompany.registrationCodeUsedDate)}</p>
                          </div>                         
                        </>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Usage Statistics */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                    <Monitor className="w-4 h-4 mr-2" />
                    Usage Statistics
                  </h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Total Users</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedCompany.userCount}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Active Users</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedCompany.activeUserCount}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Active Devices</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedCompany.deviceCount}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Company Modal */}
    {showEditModal && editingCompany && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-medium">Edit Company</h3>
              <button 
                onClick={() => {
                  setShowEditModal(false);
                  setEditingCompany(null);
                  setError(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
                <div className="flex">
                  <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
                  <span className="text-sm text-red-800">{error}</span>
                </div>
              </div>
            )}

            <form onSubmit={handleUpdateCompany} className="space-y-6">
              {/* Company Information */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-4">Company Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Company Name *</label>
                    <input
                      type="text"
                      required
                      value={editForm.companyName}
                      onChange={(e) => setEditForm({ ...editForm, companyName: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Contact Email *</label>
                    <input
                      type="email"
                      required
                      value={editForm.contactEmail}
                      onChange={(e) => setEditForm({ ...editForm, contactEmail: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Contact Phone</label>
                    <input
                      type="tel"
                      value={editForm.contactPhone}
                      onChange={(e) => setEditForm({ ...editForm, contactPhone: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Address</label>
                    <input
                      type="text"
                      value={editForm.address}
                      onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">City</label>
                    <input
                      type="text"
                      value={editForm.city}
                      onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Post Code</label>
                    <input
                      type="text"
                      value={editForm.postCode}
                      onChange={(e) => setEditForm({ ...editForm, postCode: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Billing Information - Only show for Customer companies */}
              {editingCompany.companyType === CompanyType.Customer && (
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-4">Billing Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Date of Sale</label>
                      <input
                        type="date"
                        value={editForm.dateOfSale}
                        onChange={(e) => setEditForm({ ...editForm, dateOfSale: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Monthly Rental (£)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editForm.monthlyRental || ''}
                        onChange={(e) => setEditForm({ ...editForm, monthlyRental: parseFloat(e.target.value) || 0 })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Other Charges (£)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editForm.otherCharges || ''}
                        onChange={(e) => setEditForm({ ...editForm, otherCharges: parseFloat(e.target.value) || 0 })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Devices Licensed</label>
                      <input
                        type="number"
                        min="0"
                        value={editForm.devicesLicensed || ''}
                        onChange={(e) => setEditForm({ ...editForm, devicesLicensed: parseInt(e.target.value) || 0 })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingCompany(null);
                    setError(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updating}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {updating ? 'Updating...' : 'Update Company'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    )}

    {/* Delete Confirmation Modal */}
    {showDeleteModal && companyToDelete && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-md w-full p-6">
          <div className="flex items-center mb-4">
            <AlertTriangle className="w-6 h-6 text-red-600 mr-3" />
            <h3 className="text-lg font-semibold text-gray-900">Delete Company</h3>
          </div>
          
          <div className="mb-6">
            <p className="text-gray-700 mb-3">
              Are you sure you want to delete <strong>"{companyToDelete.companyName}"</strong>?
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 text-sm font-medium mb-2">This action cannot be undone and will permanently delete:</p>
              <ul className="text-red-700 text-sm space-y-1">
                <li>• All company users and their data</li>
                <li>• All devices and device data</li>
                <li>• All assets and uploaded files</li>
                <li>• All playlists and schedules</li>
                <li>• All locations and analytics</li>
                <li>• All system status and engagement data</li>
              </ul>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => {
                setShowDeleteModal(false);
                setCompanyToDelete(null);
              }}
              disabled={isDeleting}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteCompany}
              disabled={isDeleting}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isDeleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Company
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    )}
    </div>
  );
};

export default CompanyManagement;